import { Fragment, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import useFetch from '../../../hooks/use-fetch';

import { Dialog, Transition } from '@headlessui/react';
import Form, { Field } from '@atlaskit/form';
import TextField from '@atlaskit/textfield';
import Select from '@atlaskit/select';

import ErrorsBlock from '../../../components/errors';

export default function AddDivision({closeModal, isOpen, reloadTable, tableData, parentId}) {
  const {t} = useTranslation();
  const withoutDivisionLabel = t('without_division');
  const [divisionsList, setDivisionsList] = useState([]);
  const [parent, setParent] = useState("");
  const [errors, setErrors] = useState([]);
  const [attachment, setAttachment] = useState('');
  const [{response: itemSaveResponse, isLoading: itemSaveIsLoading, error: itemSaveError}, doFetchItemSave] = useFetch(`/directories/divisions`);
  const [{response: fileUploadResponse, isLoading: fileUploadIsLoading, error: fileUploadError}, doFetchFileUpload] = useFetch(`/uploads`, true);

  useEffect(() => {
    const list = [{label: withoutDivisionLabel, value: ""}]
    tableData.forEach(it => {
      list.push({label: it.attributes.name, value: it.id})
    })
    setDivisionsList(list)
    setParent(list.find(it => it.value === parentId) || list[0])
  },[tableData, parentId, withoutDivisionLabel])

  const onSaveClick = (formData) => {
    doFetchItemSave({
      method: 'POST',
      data: {
        data: {
          name: formData.name,
          parent_id: formData.parent.value,
          cipher: formData.cipher,
          order_number: formData.order_number,
          attachment: attachment
        }
      }
    });
  };

  useEffect(() => {
    if (!fileUploadResponse) {
      return;
    }
    setAttachment({ ...fileUploadResponse });
  }, [fileUploadResponse, fileUploadIsLoading]);


  const handleFileField = (file) => {
    const formData = new FormData();
    formData.set('attachment', file);
    doFetchFileUpload({
      method: 'POST',
      data: formData
    });
  };

  useEffect(() => {
    if (itemSaveResponse) {
      closeModal();
      reloadTable();
    }
  }, [itemSaveResponse]);

  useEffect(() => {
    if (itemSaveError) {
      setErrors(itemSaveError.errors)
    }
  }, [itemSaveError]);

  useEffect(() => {
    if(!isOpen) {
      setTimeout(() => {
        setErrors([])
        setAttachment('')
      }, 300);
    }
  },[isOpen])

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog
        as="div"
        className="fixed inset-0 z-50 overflow-y-auto"
        onClose={closeModal}
      >
        <Dialog.Overlay className="fixed inset-0 bg-black opacity-30" />
        <div className="min-h-screen px-4 text-center">
          {/* <span className="inline-block h-screen align-middle" aria-hidden="true">&#8203;</span> */}
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0 scale-95"
            enterTo="opacity-100 scale-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100 scale-100"
            leaveTo="opacity-0 scale-95"
          >
            <div className="inline-block w-full max-w-md p-6 mt-32 my-8 text-left align-middle transition-all transform bg-white shadow-xl rounded">
              <Dialog.Title
                as="h3"
                className="text-lg font-medium leading-6 text-gray-900"
              >
                {t('adding_division')}
              </Dialog.Title>
              <div className="mt-2">
                <p className="text-sm text-gray-500">
                  {t('adding_division_form_text')}
                </p>
              </div>
              {errors.length > 0 ? <ErrorsBlock errors={errors}/> : ''}
              <Form onSubmit={data => onSaveClick(data)}>
                {({ formProps }) => (
                  <form {...formProps}>
                    <Field name="name" defaultValue="" label={t('division_name')} isRequired>
                      {({ fieldProps }) => <TextField {...fieldProps} />}
                    </Field>
                    <Field name="parent" defaultValue={parent} label={t('parent_division')} isRequired>
                      {({ fieldProps }) => (
                        <Select
                          options={divisionsList}
                          className="text-sm"
                          inputId="division-parent"
                          aria-label={t('parent_division')}
                          {...fieldProps}
                          onChange={(e) => setParent(e)}
                        />
                      )}
                    </Field>
                    <div className="text-sm font-medium mt-4 mb-2">{t('additional')}</div>
                    <Field name="cipher" defaultValue="" label={t('cipher')}>
                      {({ fieldProps }) => <TextField {...fieldProps} />}
                    </Field>
                    <Field name="order_number" defaultValue="" label={t('division_order_number')}>
                      {({ fieldProps }) => <TextField {...fieldProps} />}
                    </Field>
                    <div className="field mt-4">
                      <div className="relative">
                        <div className="flex flex-row justify-between items-center h-10">
                          <div className="text-sm text-gray-600 whitespace-nowrap truncate font-medium">
                            <div>{attachment ? attachment.data.attributes.filename : t('upload_division_order')}</div>
                          </div>
                          <div className="bg-gray-100 rounded text-sm text-gray-800 px-4 py-2">
                            <span className="font-medium">{t('upload')}</span>
                          </div>
                        </div>
                        <input
                          aria-label={t('upload_division_order')}
                          name="attachment"
                          type="file"
                          className="absolute cursor-pointer opacity-0 block w-full h-10 top-0"
                          onChange={(evt) => handleFileField(evt.target.files[0])}
                        />
                      </div>
                    </div>                    
                    <div className="flex justify-between mt-8">
                      <button 
                        type="submit" 
                        className="inline-flex justify-center px-4 py-2 text-sm font-medium text-blue-900 bg-blue-100 border border-transparent rounded hover:bg-blue-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-blue-500"
                      >
                        {t('save')}
                      </button>
                      <button 
                        onClick={() => closeModal()}
                        type="button" 
                        className="inline-flex justify-center px-4 py-2 text-sm font-medium text-gray-900 bg-gray-100 border border-transparent rounded hover:bg-gray-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-blue-500"
                      >
                        {t('cancel')}
                      </button>
                    </div>
                  </form>
                )}
              </Form>
            </div>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition>
  )
}
